import { Box, Flex, Text } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { KNIT_PROGRAM } from "@/content";
import { courseAtRow, fetchGzipped, parseProgram, type ProgramFormat, type ProgramText } from "./programText";

// Lines of the course before the one in focus shown above it, and lines shown in all.
const BEFORE = 6;
const WINDOW = 160;

const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

type Loading = ProgramText | "loading" | "failed";

type ProgramTextPanelProps = {
  // the DAT row in focus, counted from 0 at the bottom
  readonly row: number;
  readonly onSelectRow: (row: number) => void;
  // false until the viewer is near the screen: the text is fetched then
  readonly load: boolean;
};

// The program as text beside the DAT, LOOP's knitcode or knitout, open at the course of the row in
// focus; a course's header moves the DAT to it.
export const ProgramTextPanel = ({ row, onSelectRow, load }: ProgramTextPanelProps) => {
  const [format, setFormat] = useState<ProgramFormat>("knitcode");
  const [texts, setTexts] = useState<Partial<Record<ProgramFormat, Loading>>>({});
  const scrollRef = useRef<HTMLDivElement>(null);

  const text = texts[format];
  useEffect(() => {
    if (text || !load) return;
    setTexts((current) => ({ ...current, [format]: "loading" }));
    fetchGzipped(KNIT_PROGRAM.text[format].packed)
      .then((bytes) => parseProgram(new TextDecoder().decode(bytes), format))
      .then((parsed) => setTexts((current) => ({ ...current, [format]: parsed })))
      .catch(() => setTexts((current) => ({ ...current, [format]: "failed" })));
  }, [format, text, load]);

  const loaded = typeof text === "object" ? text : null;
  const course = loaded ? courseAtRow(loaded, row) : 0;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [course, format]);

  const lines = (() => {
    if (!loaded || !loaded.courses.length) return [];
    const { courses } = loaded;
    const start = courses[course].line;
    const end = course + 1 < courses.length ? courses[course + 1].line : loaded.lines.length;
    const from = Math.max(start - BEFORE, course > 0 ? courses[course - 1].line : 0);
    const to = Math.min(from + WINDOW, loaded.lines.length);
    const headers = new Map(courses.map((entry) => [entry.line, entry.row]));
    return loaded.lines.slice(from, to).map((line, index) => {
      const at = from + index;
      return { at, line, current: at >= start && at < end, row: headers.get(at) };
    });
  })();

  return (
    <Flex direction="column" minW={0} minH={0} h="100%">
      <Flex
        align="center"
        justify="space-between"
        gap="12px"
        px="14px"
        py="10px"
        borderBottom="1px solid"
        borderColor="line.hairline"
      >
        <Flex
          role="group"
          aria-label={KNIT_PROGRAM.textLabel}
          gap="4px"
          p="3px"
          borderRadius="full"
          bg="surface.DEFAULT"
        >
          {(["knitcode", "knitout"] as const).map((entry) => (
            <Box
              key={entry}
              as="button"
              type="button"
              aria-pressed={entry === format}
              onClick={() => setFormat(entry)}
              px="14px"
              py="6px"
              borderRadius="full"
              fontSize="small"
              lineHeight={1}
              bg={entry === format ? "surface.white" : "transparent"}
              color={entry === format ? "ink.DEFAULT" : "warm.600"}
              boxShadow={entry === format ? "0 1px 2px rgba(0, 0, 0, 0.08)" : "none"}
              transitionProperty="background-color, color"
              transitionDuration="0.2s"
              transitionTimingFunction="brand"
            >
              {KNIT_PROGRAM.text[entry].tab}
            </Box>
          ))}
        </Flex>
        <Text variant="small" color="warm.600" whiteSpace="nowrap">
          {loaded?.courses.length ? `${KNIT_PROGRAM.courseLabel} ${course + 1}` : ""}
        </Text>
      </Flex>
      <Box
        ref={scrollRef}
        flex="1"
        minH={0}
        overflow="auto"
        py="8px"
        fontFamily={MONO}
        fontSize="12px"
        lineHeight={1.55}
      >
        {text === "failed" && (
          <Text variant="small" px="14px">
            {KNIT_PROGRAM.failed}
          </Text>
        )}
        {(!text || text === "loading") && (
          <Text variant="small" px="14px">
            {KNIT_PROGRAM.loadingText}
          </Text>
        )}
        {lines.map(({ at, line, current, row: header }) => (
          <Box
            key={at}
            px="14px"
            whiteSpace="pre"
            color={current ? "ink.DEFAULT" : "warm.600"}
            bg={current ? "rgba(247, 95, 45, 0.08)" : "transparent"}
            borderLeft="3px solid"
            borderColor={current ? "brand.600" : "transparent"}
          >
            {header === undefined ? (
              line || " "
            ) : (
              <Box
                as="button"
                type="button"
                onClick={() => onSelectRow(header)}
                title={KNIT_PROGRAM.showOnDat}
                textAlign="left"
                whiteSpace="pre"
                fontWeight={600}
                _hover={{ color: "brand.600" }}
              >
                {line}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Flex>
  );
};
